import React, { useEffect, useState } from 'react';
import { batchApi } from '../apis';
import { SalvModal } from '@salvapp/ui-components';
import {
  InternalApiDataUploadError,
  InternalApiDataUploadStatus,
  InternalApiDataUploadStatusHistoryRecord,
} from '../api-internal';
import { formatEnumLabel } from '../common/helper';
import { errorMessage } from './helper';
import './DataUploadDetails.scss';
import { SalvProgressStepper, Step } from '@salvapp/ui-components';
import { formatDate } from '../common/datetime/FormattedDateTime';
import { StepType } from '@salvapp/ui-components/build/SalvProgressStepper/SalvProgressStepper.types';

interface DataUploadDetailsProps {
  dataUploadId: number | undefined;
}

export const DataUploadDetails = ({ dataUploadId }: DataUploadDetailsProps) => {
  const [dataUploadStatusHistory, setDataUploadStatusHistory] = useState<
    Array<InternalApiDataUploadStatusHistoryRecord>
  >([]);

  useEffect(() => {
    if (!dataUploadId) {
      setDataUploadStatusHistory([]);
      return;
    }
    fetchStatusHistory(dataUploadId);
  }, [dataUploadId]);

  const fetchStatusHistory = async (dataUploadId: number) => {
    const statusHistory = await batchApi.findStatusHistory({
      uploadId: dataUploadId,
    });

    setDataUploadStatusHistory(statusHistory.sort((a, b) => a.createdTime?.getTime() - b.createdTime?.getTime()));
  };

  const getGroupedErrors = (errors?: Array<InternalApiDataUploadError>): Array<Array<InternalApiDataUploadError>> => {
    const map = new Map();
    errors?.forEach((error: InternalApiDataUploadError) => {
      const key = [error.field, error.name].toString();
      const group = map.get(key) || [];
      group.push(error);
      map.set(key, group);
    });
    return Array.from(map.values());
  };

  const isLastRecord = (record: InternalApiDataUploadStatusHistoryRecord) => {
    return record === dataUploadStatusHistory.slice(-1).pop();
  };

  const getStatusMessage = (record: InternalApiDataUploadStatusHistoryRecord) => {
    switch (record.status) {
      case InternalApiDataUploadStatus.FILE_UPLOADED:
        return null;
      case InternalApiDataUploadStatus.PREPARING:
        if (record.errors?.length) return 'Following errors were identified:';

        return isLastRecord(record) ? 'Preparing the file...' : 'No errors, preparation successful';
      case InternalApiDataUploadStatus.QUEUED:
        return 'Waiting for other data uploads to complete processing';

      case InternalApiDataUploadStatus.PROCESSING:
        if (record.errors?.length) return 'Following errors were identified:';

        return isLastRecord(record) ? 'Processing the file...' : 'No errors, processing successful';

      case InternalApiDataUploadStatus.COMPLETED:
        return 'Data successfully uploaded';

      case InternalApiDataUploadStatus.FAILED:
        return `Data ${
          failedAtValidation() ? 'validation' : 'processing'
        } failed. Please fix the errors in the file and try again`;

      case InternalApiDataUploadStatus.PARTIALLY_COMPLETED:
        return 'Some rows were successfully imported to Salv, but then processing failed';
    }
  };

  const failedAtValidation = () => {
    return (
      dataUploadStatusHistory.length >= 2 &&
      dataUploadStatusHistory[dataUploadStatusHistory.length - 2].status === InternalApiDataUploadStatus.PREPARING
    );
  };

  const getErrorStatus = (record: InternalApiDataUploadStatusHistoryRecord) => {
    if (record.status === InternalApiDataUploadStatus.FAILED) return InternalApiDataUploadStatus.FAILED;
    if (record.status === InternalApiDataUploadStatus.PARTIALLY_COMPLETED)
      return InternalApiDataUploadStatus.PARTIALLY_COMPLETED;
    if (!record.errors?.length) return null;
    if (dataUploadStatusHistory.find((record) => record.status === InternalApiDataUploadStatus.PARTIALLY_COMPLETED)) {
      return InternalApiDataUploadStatus.PARTIALLY_COMPLETED;
    } else return InternalApiDataUploadStatus.FAILED;
  };

  const getStepType = (record: InternalApiDataUploadStatusHistoryRecord, isCurrentlyLastStep: boolean): StepType => {
    const errorStatus = getErrorStatus(record);

    if (record.status === InternalApiDataUploadStatus.COMPLETED) {
      return 'filled';
    } else if (errorStatus === InternalApiDataUploadStatus.FAILED) {
      return 'error';
    } else if (errorStatus === InternalApiDataUploadStatus.PARTIALLY_COMPLETED) {
      return 'pending';
    } else if (isCurrentlyLastStep) {
      return 'pending';
    } else {
      return 'filled';
    }
  };

  return (
    <>
      <SalvModal.Body>
        <SalvProgressStepper>
          {dataUploadStatusHistory.map((record: InternalApiDataUploadStatusHistoryRecord, index: number) => {
            const statusMessage = getStatusMessage(record);
            const isCurrentlyLastStep = dataUploadStatusHistory.length === index + 1;
            const stepType = getStepType(record, isCurrentlyLastStep);
            return (
              <Step
                key={index}
                header={formatEnumLabel(record.status)}
                description={formatDate(record.createdTime, true)}
                type={stepType}
              >
                {statusMessage && (
                  <div className={'m-t-12'}>
                    <div className={'text-xs m-b-4'} style={{ color: 'var(--text-medium)' }}>
                      {statusMessage}
                    </div>
                    {getGroupedErrors(record.errors).map((errorGroup: InternalApiDataUploadError[], index: number) => {
                      return (
                        <div className='error-messages' key={index}>
                          {errorMessage(errorGroup)}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Step>
            );
          })}
        </SalvProgressStepper>
      </SalvModal.Body>
    </>
  );
};

export default DataUploadDetails;
